import '../popover/popover';

import {html, css, PropertyValueMap} from "lit";
import { customElement, property, query, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import { nothing } from 'lit-html';
import { BaseComponent } from "../../features/base-component/base-component";
import store from "../../store/store";
import { PLACEMENT_BOTTOM } from '../../common/constants/bs';
import { getPriceWithCurrency } from "../../common/utils/cart-util";
import { smallSpinner } from "../../common/constants/api-constants";
import { StoreState } from "../../store/store.interface";
import { getDeletePromoCodeStatus, getPromoCode, getPromoCodeDetails, getPromoCodeExpiration, getPromoStatus} from "./promo-code.selectors";
import { ActionStatus } from "../../common/constants/action-status";
import { areProductsUnavailable, getAddToCartStatus, getCartStatus, getDiscount, getProductCurrencyCode } from "../cart/shop-cart/store/shop-cart.selectors";
import emit from "../../common/events";
import { getUserDataForUserAnalytics } from "../user-profile/user-profile.selectors";
import { updatePromoCode } from "../cart/shop-cart/thunks/update-promo-code.thunk";
import {deletePromoCode} from "../cart/shop-cart/thunks/delete-promo-code.thunks";
import { resetPromoCode } from './index';
import { getCartAnalyticsOverrides } from '../cart/shop-cart/shop-cart.analytics';

/**
 * This component allows a user to add, apply, and change the promo code.
 */
@customElement('mhecomm-promo-code')
export class PromoCode extends connect(store)(BaseComponent) {
    static styles = [css`
        #promoCodeLink{
            display: flex;
            align-items: center;
        }
        #removeAppliedPromoCode {
            color: var(--theme-gray-500);
        }
    `];

    @query('#promoCodeInput')
    input: HTMLInputElement | undefined;

    @query('#editPromoCode')
    editButton: HTMLInputElement | undefined;

    @query('#promoCodePopover')
    promoCodePopover: HTMLInputElement | undefined;

    @query('#promoCodeLink')
    promoCodeLink: HTMLInputElement | undefined;

    @query('#removeAppliedPromoCode')
    promoCodeRemove: HTMLInputElement | undefined;

    @query('#promoApplyBtn')
    promoCodeApplyButton: HTMLInputElement | undefined;

    @query('#promoDiscountValue')
    promoDiscountValue: HTMLElement | undefined;

    /**
     * The discount of the currently applied promo code.
     * If no valid promo code is currently applied this could be undefined.
     */
    @state()
    private promoDiscount?: number;

    @property()
    storeCode?: string;

    @state()
    private isDiscountInput = false;

    @state()
    private isDiscountComputed = false;

    @state()
    private isPromoCodeError = false;

    @state()
    private isApplyDisabled = true;

    @state()
    private cartSubtotal: number | undefined;

    @state()
    private isLoading = false;

    @state()
    private currencyCode;

    private appliedPromoCode: string | undefined;

    private appliedPromoCodeDetails: string | undefined;

    private collapse: any;

    @state()
    private removePromoCode: boolean | undefined;

    renderComp() {
        return html`
            <div part="cart-promo-container">
                <div class="d-flex justify-content-between align-items-end">
                    Promo Code:
                    <button
                        class="btn btn-link p-0 fs-small ${this.isDiscountComputed || this.isDiscountInput ? 'd-none': ''}"
                        data-bs-target="#promoCollapseSection"
                        data-bs-toggle="collapse"
                        aria-controls="promoCollapseSection"
                        aria-expanded="false"
                        id="promoCodeLink"
                        @click=${this._handleCollapseShow}
                        @keyup=${this._handleCollapseShow}>
                        Enter Promo Code
                    </button>
                </div>
                <div class="mt-2 row collapse ${this.isDiscountComputed && !this.isDiscountInput ? 'd-none': ''}" id="promoCollapseSection">
                    <div class="col-12 col-md-8">
                        <input
                          type="text"
                          class="form-control ${this.isPromoCodeError ? 'is-invalid' : ''}"
                          ?disabled=${this.isLoading}
                          id="promoCodeInput"
                          aria-label="promo code"
                          aria-describedby="promoCodeError"
                          aria-invalid=${this.isPromoCodeError}
                          @keyup=${this._handlePromoCodeUpdate}
                          placeholder="Enter promo code"
                        />
                        ${this.isPromoCodeError ? html`
                            <p id="promoCodeError"
                               class="my-2 invalid-feedback d-block"
                               role="alert"
                               aria-live="assertive">
                                ${this.uxtIconReady ? html`<uxt-icon icon="exclamation-triangle" aria-hidden="true"></uxt-icon>` : ''}
                                <span class="visually-hidden">Error: </span>
                                The promo code is not valid.
                            </p>
                        ` : html`
                            <p id="promoCodeError" class="visually-hidden"></p>
                        `}
                    </div>
                    <div class="col-12 col-md-4">
                        <button class="btn btn-primary w-100 mt-2 mt-md-0" ?disabled=${this.isApplyDisabled} id="promoApplyBtn" @click=${this._calculateDiscount}>
                            ${ this.isLoading ? html`
                                <div class="spinner-border spinner-border-sm" role="status"></div>
                            ` : 'Apply' }
                        </button>
                    </div>
                </div>
                <div class="d-flex align-items-center justify-content-between align-items-end mt-2 ${!this.isDiscountComputed || this.isDiscountInput ? 'd-none': ''}">
                    <span class="col-xl-9 col-lg-8 col-8 col-sm-10 ${!this.isDiscountComputed || this.isPromoCodeError? 'd-none': ''}">
                        <span id="editPromoCode" class="mw-100 d-inline-flex align-items-center p-2 border border-1 rounded fs-small ms-1 text-decoration-none text-uppercase text-nowrap">
                            ${this.uxtIconReady ? html`<uxt-icon icon="tag" class="pe-2"></uxt-icon>`: ''}
                            <span class="text-truncate">${this.appliedPromoCode}</span>
                            <button 
                              id="removeAppliedPromoCode" 
                              aria-label="Remove applied promo code"
                              data-automation-id="btn-remove-promo-code"
                              @keyup=${this._handleRemove} 
                              @click=${this._handleRemove}
                              class="btn btn-link p-0 border-0 ms-1"> 
                                ${this.uxtIconReady ? html`<uxt-icon icon="x-lg" ></uxt-icon>`: ''}
                            </button>
                        </span>
                        ${this.appliedPromoCodeDetails ? html`<mhecomm-popover
                            id="promoCodePopover"
                            class="d-inline-block fs-small ms-2 me-3 pt-2 pb-2"
                            placement=${PLACEMENT_BOTTOM}
                            label="See Details"
                            @click=${this._emitDiscountDetails}
                            text=${this.appliedPromoCodeDetails}>
                        </mhecomm-popover>` : nothing}
                    </span>
                    <span
                        id="promoDiscountValue"
                        class="d-block col-xl-3 col-lg-4 col-4 col-sm-2 fs-large fw-bold text-nowrap text-right"
                        data-automation-id="promo-code-value"
                        role="text"
                        tabindex="-1">
                        ${this.isDiscountComputed && this.isLoading ? smallSpinner() : html`
                            <span class="visually-hidden">Promo code discount: minus </span>
                            <span aria-hidden="true">- </span>${getPriceWithCurrency(this.currencyCode, this.promoDiscount!)}
                        `}
                    </span>
                </div>
            </div>
            <p
                aria-live="polite"
                role="status"
                aria-atomic="true"
                class="visually-hidden">
                ${this.isLoading ? 'Loading promo code discount...' : nothing}
            </p>
            ${this.renderRemovePromoCodeToast()}
        `;
    }

    protected firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
        super.firstUpdated(_changedProperties);
    }

    protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
        super.updated(_changedProperties);
        this.collapse = this.bsInit('#promoCollapseSection', window.bootstrap?.Collapse, { toggle: false })?.instance;
        if(_changedProperties.has("isDiscountComputed")) {
            this.updateComplete.then(() => {
                // Focus the discount text when successfully applied for screen reader announcement
                if (this.isDiscountComputed && !this.isLoading && this.promoDiscountValue) {
                    this.promoDiscountValue.focus();
                }
            })
        }
        if(_changedProperties.has("promoCodeLink")) {
            this.updateComplete.then(() => {
                if (this.promoCodeLink) {
                    this.promoCodeLink.focus();
                }
            })
        }
        if(_changedProperties.has("isPromoCodeError")) {
            this.updateComplete.then(() => {
                if (this.input) {
                    this.input.focus();
                }
            })
        }
    }

    stateChanged(state: StoreState) {
        this.promoDiscount = getDiscount(state);
        this.appliedPromoCode = getPromoCode(state);
        this.appliedPromoCodeDetails = getPromoCodeDetails(state) ?? "";
        this.isLoading = getCartStatus(state) === ActionStatus.loading || getPromoStatus(state) === ActionStatus.loading;
        this.isApplyDisabled = (this.isLoading || areProductsUnavailable(state));
        this.currencyCode = getProductCurrencyCode(state);

        if (getPromoStatus(state) === ActionStatus.succeeded) {
            if (this.appliedPromoCode) {
                this.isPromoCodeError = false;
                this.isDiscountComputed = true;
                this.isDiscountInput = false;
            } else {
                this.isPromoCodeError = false;
                this.isDiscountComputed = false;
                this.isDiscountInput = false;
            }
        } else if (getPromoStatus(state) === ActionStatus.failed) {
            if (state.promoCodeDiscountState.updatePromoCode?.error) {
                this.isDiscountComputed = false;
                this.isDiscountInput = true;
                this.isPromoCodeError = true;
                if (this.editButton) {
                    this.editButton.focus();
                }
            }
        }

        if (getDeletePromoCodeStatus(state) === ActionStatus.succeeded && getAddToCartStatus(state) === ActionStatus.succeeded) {
            this._handleRemovePromoCode();
            this._handleRemovePromoDiscount();
        }

        this.cartSubtotal = state.shopCartState?.getShopCart.data?.totalAmount;
    }

    /**
     * Dispatches an update to the Redux store to update the promo code
     * and apply it if valid
     */
    private _calculateDiscount() {
        this.removePromoCode = false;
        emit({
            element: this,
            name: 'click',
            detail: this.dataLayerDetail('click-apply-promo-code-button', 'Apply'),
        });

        store.dispatch(updatePromoCode({ promoCode: this.currentPromoCode(), storeCode: this.storeCode} )).then(() => {
            emit({
                element: this,
                name: 'click',
                detail: this.dataLayerDetail('calculate-promo-code-discount', 'Promo code calculation'),
            });
        });
    }

    /**
     * Dispatches an event that emits a datalayer event to record the promo code discount details
     */
    private _emitDiscountDetails() {
        emit({
            element: this,
            name: 'click',
            detail: this.dataLayerDetail('promo-code-discount-details', 'See Details'),
        });
    }

    private dataLayerDetail(name: string, label: string) {
        const state = store.getState() as StoreState;

        return {
            label,
            eventName: name,
            promoCode: getPromoCode(state),
            promoCodeDetails: getPromoCodeDetails(state),
            promoCodeExpiration: getPromoCodeExpiration(state),
            url: window.location.href,
            user: getUserDataForUserAnalytics(state),
            comp:"mhecomm-promo-code",
            ...getCartAnalyticsOverrides(state)
        };
    }

    /**
     * @returns The promo code that the user has typed into the input field
     *          Note that this might be different than the promo code that is
     *          currently applied on the page because the user might have changed
     *          the input field since applying the promo code.
     */
    currentPromoCode() {
        return this.input ? this.input.value : '';
    }

    _handleCollapseShow(event) {
        if (event && (event.type == 'click' || (event.type == 'keyup' && event.code == 'Space'))) {
            this.collapse.show();

            this.isDiscountInput = true;
            this.isDiscountComputed = false;

            const promoLink = this.shadowRoot!.querySelector('#promoCodeLink') as HTMLLinkElement;
            promoLink.setAttribute('aria-expanded', 'true');

            this.input?.focus();

            emit({
                element: this,
                name: 'click',
                detail: this.dataLayerDetail('click-edit-promo-code-link', 'Enter Promo Code'),
            });
            
        }
    }

    _handleCollapseHide(event, force: boolean | null) {
        if ((event && (event.type == 'click' || (event.type == 'keyup' && event.code == 'Space'))) || force === true) {
            this._handleRemovePromoDiscount();
        }
    }

    _handleRemovePromoCode() {
        store.dispatch(resetPromoCode());
        this.appliedPromoCode = undefined;
        this.appliedPromoCodeDetails = undefined;
        const promoInputElem = this.shadowRoot!.querySelector('#promoCodeInput') as HTMLInputElement;
        promoInputElem.value = '';
    }

    _handleRemovePromoDiscount() {
        this.collapse.hide();
        this.isDiscountInput = false;
        this.isDiscountComputed = false;
        const promoLink = this.shadowRoot!.querySelector('#promoCodeLink') as HTMLLinkElement;
        this.updateComplete.then(() => {
            promoLink.setAttribute('aria-expanded', 'false');
            promoLink.focus();
        })
    }

    /**
     * Called when a user clicks on the <a> tag clear promo code link.
     */
    _handleRemove(event) {
        if (event && (event.type == 'click' || (event.type == 'keyup' && event.code == 'Enter'))) {
            this.removePromoCode = true;
            emit({
                element: this,
                name: 'click',
                detail: this.dataLayerDetail('remove-promo-code-discount', 'Remove applied promo code'),
            });
            store.dispatch(deletePromoCode({ storeCode: this.storeCode} )).then(() => {
                this._handleRemovePromoCode();
                this._handleCollapseHide(event, false);
            });
            if (this.promoCodeLink) {
                this.promoCodeLink.focus();
            }
        }
    }

    _handlePromoCodeUpdate(event) {
        if (event && event.type == 'keyup' && event.code == 'Enter' && this.currentPromoCode()?.length >= 1) {
            this._calculateDiscount();
            return;
        }

        if (event && event.type == 'keyup' && event.code == 'Escape') {
            store.dispatch(resetPromoCode());
            this._handleCollapseHide(event, true);
            const promoInputElem = this.shadowRoot!.querySelector('#promoCodeInput') as HTMLInputElement;
            promoInputElem.value = ''
            emit({
                element: this,
                name: 'click',
                detail: this.dataLayerDetail('cancel-promo-code-discount-entry', 'Cancel Promo Code'),
            });
            return;
        }

        this.isPromoCodeError = false;
        if (this.currentPromoCode()?.length < 1) {
            this.isApplyDisabled = true;
        } else {
            this.isApplyDisabled = false;
        }
    }

    renderRemovePromoCodeToast() {
        return this.removePromoCode && this.appliedPromoCode !== undefined ? html `
            <mhecomm-toast
            type="secondary"
            header="Promo Code Removed"
            .body="${this.getRemovedPromoCodeMessage()}"
            ?autoHide="${false}"
            @toast-close="${() => undefined}"
            ></mhecomm-toast>
        ` : nothing;
    }

    getRemovedPromoCodeMessage() {
        return html `<span>Promo code ${this.appliedPromoCode} successfully removed.</span>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'mhecomm-promo-code': PromoCode;
    }
}
